import { browser } from '$app/environment';
import { getCookie } from '../cookies.client';

// PostHog will be imported dynamically to avoid build issues in different environments
let posthog: any;
let initialized = false;

// Configuration interface for PostHog
export interface PostHogConfig {
  apiKey: string;
  apiHost: string;
  capturePageview?: boolean;
  capturePageleave?: boolean;
}

/**
 * Initialize PostHog only in the browser
 * Each app should call this with their environment variables
 */
export const initPostHog = async (config: PostHogConfig) => {
  if (!browser || initialized) return posthog;

  try {
    // Dynamically import PostHog only in the browser
    if (!posthog) {
      const module = await import('posthog-js');
      posthog = module.default;
    }

    if (!config.apiKey) {
      console.warn('PostHog API key not provided');
      return posthog;
    }

    posthog.init(
      config.apiKey,
      {
        api_host: config.apiHost,
        capture_pageview: config.capturePageview ?? false,
        capture_pageleave: config.capturePageleave ?? false
      }
    );

    // Identify user if __agapi_identifier cookie exists
    identifyUserFromCookie();

    initialized = true;
    return posthog;
  } catch (error) {
    console.error('Failed to initialize PostHog:', error);
    return posthog;
  }
}

/**
 * Identifies the current user using the __agapi_identifier cookie if available
 * This links anonymous events to a specific user ID
 */
export const identifyUserFromCookie = () => {
  if (!browser || !posthog || !posthog.__loaded) return;

  const identifier = getCookie<string>('__agapi_identifier');
  if (identifier) {
    // Use the cookie value as the distinct ID without additional properties
    posthog.identify(identifier);
    console.debug('PostHog: User identified with cookie identifier');
  }
}

/**
 * Initialize PostHog from environment variables
 * This is a convenience method that pulls PUBLIC_POSTHOG_API_KEY and PUBLIC_POSTHOG_HOST
 * from the environment.
 */
export const initPostHogFromEnv = async () => {
  try {
    // Use dynamic import with eval to avoid build issues
    // This is a workaround since static imports of '$env/static/public'
    // can cause build issues if the variables aren't defined
    const { PUBLIC_POSTHOG_API_KEY, PUBLIC_POSTHOG_HOST } = await import('$env/static/public');

    return initPostHog({
      apiKey: PUBLIC_POSTHOG_API_KEY,
      apiHost: PUBLIC_POSTHOG_HOST,
      capturePageview: false,
      capturePageleave: false
    });
  } catch (error) {
    console.error('Failed to initialize PostHog from environment:', error);
    return null;
  }
}

/**
 * Helper function to safely capture events
 * This will not throw if PostHog is not initialized
 */
export const captureEvent = (eventName: string, properties?: Record<string, any>) => {
  if (browser && posthog && posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
}

/**
 * Helper function to check if a feature flag is enabled
 * This will return the default value if PostHog is not initialized
 */
export const isFeatureEnabled = (featureFlag: string, defaultValue: boolean = false): boolean => {
  if (!browser || !posthog || !posthog.__loaded) return defaultValue;
  return posthog.isFeatureEnabled(featureFlag) ?? defaultValue;
}

/**
 * Get the PostHog instance for direct access
 * Will be null if not initialized
 */
export const getPostHog = () => {
  return posthog;
}

/**
 * Reset the current user's identity
 * Should be called on logout to prevent shared computer issues
 */
export const resetIdentity = (resetDeviceId: boolean = false) => {
  if (browser && posthog && posthog.__loaded) {
    posthog.reset(resetDeviceId);
  }
}

/**
 * Setup page tracking with PostHog
 * This should be called in a +layout.svelte file
 */
export const setupPageTracking = (beforeFn: () => void, afterFn: () => void) => {
  if (browser && posthog && posthog.__loaded) {
    beforeFn();
    afterFn();
  }
}